import * as swcHelpers from '@swc/helpers';
import crypto from 'crypto';
import { ApolloClient, from, InMemoryCache } from '@apollo/client';
import { createPersistedQueryLink } from '@apollo/client/link/persisted-queries';
import { errorLink, timeoutLink, forwardHeadersLink, authLink, httpLink } from './links.js';
import { formatClientVersionInfo } from './utils.js';

let apolloClient;
const sha256 = (input)=>crypto.createHash('sha256').update(input).digest('hex')
;
const initApolloClient = (uri, options)=>{
    // Make sure to create a new client for every server-side request so that data
    // isn't shared between connections
    if (typeof window === 'undefined') {
        return createApolloClient(uri, options);
    }
    if (!apolloClient) {
        apolloClient = createApolloClient(uri, options);
    }
    return apolloClient;
};
const createApolloClient = (uri, options = {})=>{
    var ref;
    if (!uri) {
        console.error('No graphQL endpoint specified.');
        throw new Error('No graphQL endpoint specified.');
    }
    const cacheConfig = options.cacheConfig || {};
    const initialState = options.initialState || {};
    const queryOptions = options.queryOptions || {};
    const headers = {};
    const persistedQueriesLink = createPersistedQueryLink({
        sha256,
        useGETForHashedQueries: true
    });
    const clientInfo = options === null || options === void 0 ? void 0 : options.clientInfo;
    const name = ((ref = options.clientInfo) === null || ref === void 0 ? void 0 : ref.name) || '';
    const version = formatClientVersionInfo(clientInfo === null || clientInfo === void 0 ? void 0 : clientInfo.versions);
    return new ApolloClient({
        ssrMode: typeof window !== 'undefined',
        link: from([
            errorLink,
            timeoutLink(10000),
            forwardHeadersLink,
            authLink(options),
            ...queryOptions.isPersistentQueryDisabled || process.env.NEXT_PUBLIC_API_MOCKING === 'true' ? [] : [
                persistedQueriesLink
            ],
            httpLink({
                headers,
                uri
            }), 
        ]),
        defaultOptions: {
            watchQuery: {
                nextFetchPolicy (currentFetchPolicy) {
                    return process.env.NEXT_PUBLIC_API_MOCKING === 'true' ? 'no-cache' : currentFetchPolicy;
                }
            }
        },
        cache: new InMemoryCache(swcHelpers.objectSpread({
            possibleTypes: {
                Product: [
                    'AccessoriesProduct',
                    'ContactLensProduct',
                    'FrameProduct',
                    'LensTypeProduct',
                    'PrescriptionGlassesPackageOptionsProduct',
                    'PrescriptionGlassesPackageProduct',
                    'ReadyReaderProduct',
                    'SolutionProduct',
                    'SunglassesProduct', 
                ],
                LineItem: [
                    'LineItemBundle',
                    'LineItemContactLenses',
                    'LineItemRegular'
                ],
                TypeDetails: [
                    'BundleNonPrescriptionBundleTypeDetails',
                    'BundlePrescriptionGlassesTypeDetails',
                    'ContactLensBundleBeneluxTypeDetails',
                    'ContactLensSubscriptionTypeDetails',
                    'ContactLensesTypeDetails',
                    'RegularTypeDetails', 
                ],
                IValidationError: [
                    'SolvableValidationError',
                    'ValidationError'
                ],
                Variant: [
                    'AccessoriesVariant',
                    'ContactLensVariant',
                    'FrameVariant',
                    'LensTypeVariant',
                    'PrescriptionGlassesPackageOptionsVariant',
                    'PrescriptionGlassesPackageVariant',
                    'ReadyReaderVariant',
                    'SolutionVariant',
                    'SunglassesVariant', 
                ],
                OrderLineItem: [
                    'OrderLineItemBundle',
                    'OrderLineItemContactLens',
                    'OrderLineItemNonPrescriptionBundle',
                    'OrderLineItemPrescriptionGlass',
                    'OrderLineItemRegular',
                    'OrderLineItemSolution',
                    'OrderLineItemSubscription', 
                ],
                OrderedBundledLineItem: [
                    'OrderLineItemContactLens',
                    'OrderLineItemSolution'
                ],
                PrescriptionGlassPrescription: [
                    'AddedGlassesPrescription',
                    'EmailPrescription',
                    'ExistingPrescription',
                    'FilePrescription',
                    'OCRPrescription', 
                ]
            },
            typePolicies: {
                OrderedProductPrescriptionGlass: {
                    keyFields: [
                        'sku'
                    ]
                },
                LegacyProduct: {
                    keyFields: [
                        'id',
                        'sku'
                    ]
                },
                Variant: {
                    keyFields: [
                        'sku'
                    ]
                },
                ContactLensVariant: {
                    keyFields: [
                        'sku'
                    ]
                },
                GetReturnResponse: {
                    keyFields: (object)=>{
                        // We need to fallback to an empty string if the return object is null
                        // Otherwise Apollo will throw an error
                        if (!object.return) {
                            return '';
                        }
                        return [
                            'return',
                            [
                                'order',
                                [
                                    'orderNumber'
                                ]
                            ]
                        ];
                    }
                },
                ContactLensProduct: {
                    keyFields: [
                        'id'
                    ]
                },
                OnlineOrdersData: {
                    fields: {
                        results: {
                            merge (existing = [], incoming, { readField  }) {
                                if (!existing.length) {
                                    // Don't merge when `existing` is empty or when the `limit` variable is undefined.
                                    return incoming;
                                }
                                /**
                 * Merge paginated orders, used by the useGetMyOrderHistory. This function doesn't return duplicate orders.
                 * We have seen cases where multiple queries were called and duplicate orders were displayed.
                 */ const merged = [
                                    ...existing
                                ];
                                incoming.forEach((item)=>{
                                    const i = merged.findIndex((value)=>readField('orderNumber', value) === readField('orderNumber', item)
                                    );
                                    if (i === -1) {
                                        // Order is new
                                        merged.push(item);
                                    } else {
                                        // Order already exists
                                        merged[i] = item;
                                    }
                                });
                                return merged;
                            }
                        }
                    }
                },
                Order: {
                    // Cache orders based on the unique `orderNumber`
                    keyFields: [
                        'orderNumber'
                    ]
                },
                Customer: {
                    keyFields: [
                        'id'
                    ],
                    fields: {
                        orders: {
                            // Don't cache based on the variables `limit` and `offset`
                            keyArgs: false
                        },
                        me: {
                            merge (existing = [], incoming, { mergeObjects  }) {
                                return mergeObjects(existing, incoming);
                            }
                        }
                    }
                },
                SolutionVariant: {
                    keyFields: [
                        'sku'
                    ],
                    fields: {
                        product: {
                            // Custom merge function tells apollo its safe to merge SolutionProducts which are direct descendants of SolutionVariants, this effectively uses the parent SolutionVariant 'gan' as the unique identifier for SolutionProduct
                            // https://www.apollographql.com/docs/react/caching/cache-field-behavior/#merging-non-normalized-objects
                            merge (existing, incoming) {
                                return swcHelpers.objectSpread({}, existing, incoming);
                            }
                        }
                    }
                },
                DiscountPrice: {
                    keyFields: [
                        'amount',
                        'appliedDiscounts'
                    ]
                },
                CartDiscount: {
                    keyFields: [
                        'amount'
                    ]
                }
            }
        }, cacheConfig)).restore(initialState),
        name,
        version
    });
};

export { createApolloClient, initApolloClient as default };
//# sourceMappingURL=client.js.map
